import type { SubmissionRow } from '../api/client';
import { registryFieldsOf, registryValue, type Lookup } from './presenters';

/**
 * Starter text for a reply, per form.
 *
 * The participant-facing bodies are pack 03's outbound templates
 * (reference/studies/03_ENACT_Website_Workflows_and_Outbound_Templates.docx)
 * verbatim, with its bracketed slots turned into `{{name}}`, `{{study}}` and
 * `{{email}}` where the row can fill them. Slots the row cannot fill —
 * `[patient label]`, the clinician's contact choice — stay bracketed on
 * purpose: a patient's name is never auto-inserted into a clinician's email,
 * and a bracket in a draft is seen before it is sent.
 *
 * The forms pack 03 has no template for (fit questions, study alerts, status
 * pings, media, training, contact preferences) get a short starter written
 * here from each form's own plain-language line. They are drafts to edit, not
 * approved copy — redline them like the rest.
 *
 * The rule from pack 03 §6 applies to every one of these: no eligibility
 * impressions, scores or internal reasons in anything sent.
 *
 * No address or footer here: the API sends every reply with the lab's
 * footer (address, phone, 988) under it, as it does a receipt.
 *
 * Presentation for now, like badge colours. If the lab wants to edit these
 * without a deploy, they move to `/meta` and this file becomes a lookup.
 */
export interface ReplyTemplate {
  key: string;
  label: string;
  subject: string;
  body: string;
}

export interface TemplateContext {
  name: string;
  study: string;
  email: string;
}

const A_RECEIVED: ReplyTemplate = {
  key: 'received',
  label: 'Inquiry received',
  subject: 'We received your ENACT inquiry',
  body:
    'Hello {{name}}. Thank you for writing to ENACT at the Yale Program for Psychedelic Science. This message only confirms we have your request.\n\nNext: a coordinator will contact you about an introductory call if that is the right step. Nothing in this email enrolls you or offers treatment.\n\nIf you are in immediate danger, call 988 or 911.',
};

const B_EARLY_INTEREST: ReplyTemplate = {
  key: 'early-interest',
  label: 'Early interest received',
  subject: "You're on the ENACT early-interest list",
  body:
    'Hello {{name}}. We will contact you if {{study}} opens for screening. This list is not an application.\n\nIf you want to talk about a study that is recruiting now, reply to this email or use Book an intake call.',
};

const C_CLINICIAN_ACK: ReplyTemplate = {
  key: 'clinician-ack',
  label: 'Clinician acknowledgment',
  subject: 'We received your ENACT referral',
  body:
    'Dear {{name}}, thank you for referring [patient label] to ENACT / YPPS. We will [contact the patient / wait until they expect us / reply to you only], as you indicated.\n\nWe cannot share later clinical detail without the participant’s permission. For urgent clinical issues, use your own emergency pathways — this inbox is not on-call care.',
};

const D_PARTNERSHIP: ReplyTemplate = {
  key: 'partnership',
  label: 'Note received',
  subject: 'ENACT received your note',
  body:
    'Thank you. Someone from leadership will reply within a few business days.\n\nIf the matter is time-sensitive, write enact-research@enactresearch.com with your deadline in the subject line.',
};

const NOT_A_MATCH: ReplyTemplate = {
  key: 'not-a-match',
  label: 'Not a match',
  subject: 'Update on your ENACT inquiry',
  body:
    'Hello {{name}}. Thank you for the time you gave us. After review, this study is not the right match. That is not a statement about your care and not a diagnosis from ENACT.\n\nIf you asked to stay on a notify list, we will keep {{email}} for future studies you selected. For treatment, please keep working with your own clinician. If you are in crisis, call 988.\n\nWith appreciation,\nThe ENACT team',
};

/** Pack 03 §7 — sent only when the clinician referred the patient and sharing this level of status is allowed. */
const outcome = (key: string, label: string, line: string): ReplyTemplate => ({
  key: `outcome-${key}`,
  label: `Outcome: ${label}`,
  subject: 'Update on your ENACT referral',
  body: `Dear {{name}}, ${line}\n\nWe cannot share clinical detail beyond what the participant has allowed.`,
});

const OUTCOMES: ReplyTemplate[] = [
  outcome('screening', 'Screening', 'we have been in touch and screening is underway. We will not share clinical detail.'),
  outcome('enrolled', 'Enrolled', 'your patient has enrolled in {{study}}. Please keep providing their ordinary care unless a study clinician writes to coordinate.'),
  outcome('waitlist', 'Waitlist', 'this is not a current opening. We kept their interest on file with their permission.'),
  outcome('not-a-match', 'Not a match', 'this study is not the right match. We encouraged them to remain in ordinary care with you.'),
  outcome('more-info', 'Need more info', 'could we schedule a 10-minute call? We do not have enough to advise on fit.'),
];

/* Starters for the forms pack 03 does not cover, from each form's own plain-language line. */

const FIT_REPLY: ReplyTemplate = {
  key: 'fit',
  label: 'Fit question — reply',
  subject: 'Your ENACT fit question',
  body:
    'Dear {{name}}, thank you for asking before referring.\n\n[Say whether this kind of patient is in scope for {{study}}, and why not if not.]\n\nWe have not opened a patient file from your note. If you would like us to contact a person, use Refer a patient. We cannot advise on an individual’s care from a de-identified sketch.',
};

const ALERT_CONFIRMED: ReplyTemplate = {
  key: 'alert',
  label: 'Alert confirmed',
  subject: 'Your ENACT study alert is set',
  body:
    'Dear {{name}}, your alert is set. We will email this address when {{study}} starts taking inquiries.\n\nThis is not a referral and not a place on a participant list. Every message we send has a link to stop.',
};

const PING_RECEIVED: ReplyTemplate = {
  key: 'ping-received',
  label: 'Referral found',
  subject: 'Update on your ENACT referral',
  body:
    'Dear {{name}}, yes — we have your referral on file. We can say only what you and the person already allowed.\n\n[Status line, if sharing it is permitted.]',
};

const MEDIA_REPLY: ReplyTemplate = {
  key: 'media',
  label: 'Media inquiry — reply',
  subject: 'Your ENACT media inquiry',
  body:
    'Hello {{name}}, thank you for reaching out.\n\n[Interview, fact-check or study status — what we can offer, and when.]\n\nWe do not comment on individual participants or unpublished primary results.',
};

const TRAINING_REPLY: ReplyTemplate = {
  key: 'training',
  label: 'Training inquiry — reply',
  subject: 'Your ENACT training inquiry',
  body:
    'Hello {{name}}, thank you for your interest in training with ENACT.\n\n[Reply about the track and timing they asked about.]\n\nTraining does not certify independent psychedelic practice, and no deadlines or cohort sizes are posted yet.',
};

const PREFS_APPLIED: ReplyTemplate = {
  key: 'prefs',
  label: 'Preferences applied',
  subject: 'How we will reach you',
  body:
    'Hello {{name}}, we have saved how and when to reach you, and we will use those choices the next time we write or call. This does not decide whether a study is a fit.',
};

export const TEMPLATES_BY_FORM: Record<string, ReplyTemplate[]> = {
  intake: [A_RECEIVED, NOT_A_MATCH],
  inquiry: [A_RECEIVED],
  waitlist: [B_EARLY_INTEREST],
  'early-interest': [B_EARLY_INTEREST],
  referral: [C_CLINICIAN_ACK, ...OUTCOMES],
  'referral-status': [PING_RECEIVED, ...OUTCOMES],
  'pre-referral-fit': [FIT_REPLY, OUTCOMES[4]!],
  'clinician-alerts': [ALERT_CONFIRMED],
  partnership: [D_PARTNERSHIP],
  media: [D_PARTNERSHIP, MEDIA_REPLY],
  fellowship: [D_PARTNERSHIP, TRAINING_REPLY],
  'how-to-reach-me': [PREFS_APPLIED],
  // A stop request is answered by stopping, not by mail.
  'stop-contacting-me': [],
};

export const templatesFor = (form: string): ReplyTemplate[] => TEMPLATES_BY_FORM[form] ?? [];

/**
 * Fill the slots. An empty value leaves `[name]` in the text rather than a
 * blank, so a missing first name is noticed in the draft and not in the inbox.
 */
export function fillTemplate(text: string, ctx: TemplateContext): string {
  return text.replace(/\{\{(name|study|email)\}\}/g, (_m, k: keyof TemplateContext) => ctx[k] || `[${k}]`);
}

/** The slots a row can fill: first name, the study or studies it names, the email. */
export function contextOf(r: SubmissionRow, L: Lookup): TemplateContext {
  const d = r.data as Record<string, unknown>;
  const clean = (v: string) => (v === '—' ? '' : v);

  let study = '';
  const fields = registryFieldsOf(r);
  if (fields) {
    // A clinician names one study; an interest list names several.
    study =
      clean(registryValue('study.slug', fields['study.slug'], L)) ||
      clean(registryValue('interest.topics[]', fields['interest.topics[]'], L)) ||
      clean(registryValue('routing.interest[]', fields['routing.interest[]'], L));
  } else if (Array.isArray(d.studies)) {
    study = (d.studies as string[]).map((k) => clean(L.studyLabel(k))).filter(Boolean).join(' / ');
  } else if (typeof d.study === 'string' && d.study) {
    study = clean(L.studyLabel(d.study));
  }

  return {
    name: r.redacted ? '' : (r.name.trim().split(/\s+/)[0] ?? ''),
    study,
    email: r.redacted ? '' : r.email,
  };
}
